import { useNavigate } from 'react-router-dom'
import logo from '../../assets/logo.svg'
import './StaticPage.css'
import { PwaUpdateBanner } from '../components/PwaUpdateBanner'

export function Privacy() {
  const navigate = useNavigate()

  return (
    <div className="static-page">
      <header className="static-header">
        <div className="static-header-content">
          <img 
            src={logo} 
            alt="Ordelio Logo" 
            className="static-logo clickable" 
            onClick={() => navigate('/')}
          />
          <button onClick={() => navigate('/')} className="back-to-home-btn">
            ← Back to Home
          </button>
        </div>
      </header>

      <PwaUpdateBanner />

      <main className="static-content">
        <article className="content-container">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: February 2026</p>
          
          <section className="content-section">
            <h2>Introduction</h2>
            <p>
              Ordelio ("we," "our," or "us") is committed to protecting your privacy. This Privacy 
              Policy explains how we collect, use, and safeguard your information when you use our 
              list and checklist application.
            </p>
          </section>

          <section className="content-section">
            <h2>Information We Collect</h2>
            
            <h3>Account Information</h3>
            <p>When you sign in with Google, we collect:</p>
            <ul>
              <li>Your name</li>
              <li>Email address</li>
              <li>Profile picture (if available)</li>
              <li>Google user ID</li>
            </ul>

            <h3>List Data</h3>
            <p>When you use Ordelio, we store:</p>
            <ul>
              <li>Scenarios you create (names and descriptions)</li>
              <li>Shopping lists and items</li>
              <li>Flow configurations (store routes)</li>
              <li>Collaboration settings (who you share scenarios with)</li>
              <li>Item completion status</li>
            </ul>

            <h3>Technical Information</h3>
            <p>We automatically collect certain technical data:</p>
            <ul>
              <li>Device type and browser information</li>
              <li>IP address</li>
              <li>Usage patterns and app performance metrics</li>
              <li>Error logs and debugging information</li>
            </ul>
          </section>

          <section className="content-section">
            <h2>How We Use Your Information</h2>
            <p>We use the collected information to:</p>
            <ul>
              <li>Provide and maintain the Ordelio service</li>
              <li>Sync your shopping lists across devices</li>
              <li>Enable real-time collaboration features</li>
              <li>Provide smart item suggestions based on your history</li>
              <li>Improve app performance and fix bugs</li>
              <li>Send important service notifications</li>
              <li>Analyze usage patterns to improve features</li>
            </ul>
          </section>

          <section className="content-section">
            <h2>Data Storage and Security</h2>
            <p>
              Your data is stored securely in Microsoft Azure cloud infrastructure using industry-standard 
              security practices:
            </p>
            <ul>
              <li><strong>Encryption:</strong> All data is encrypted in transit (TLS/SSL) and at rest</li>
              <li><strong>Authentication:</strong> We use Google OAuth 2.0 for secure authentication</li>
              <li><strong>Access Control:</strong> Only you and people you explicitly invite can access your scenarios</li>
              <li><strong>Backups:</strong> Regular automated backups ensure data reliability</li>
            </ul>
          </section>

          <section className="content-section">
            <h2>Data Sharing</h2>
            <p>
              We do not sell, trade, or rent your personal information to third parties. Your data is shared only:
            </p>
            <ul>
              <li>With collaborators you explicitly invite to your scenarios</li>
              <li>With service providers who help us operate the app (Azure, Google OAuth)</li>
              <li>When required by law or to protect our legal rights</li>
            </ul>
          </section>

          <section className="content-section">
            <h2>Your Rights and Choices</h2>
            <p>You have the right to:</p>
            <ul>
              <li><strong>Access:</strong> View all data we have about you</li>
              <li><strong>Update:</strong> Modify your shopping lists and scenarios at any time</li>
              <li><strong>Delete:</strong> Remove scenarios, lists, or your entire account</li>
              <li><strong>Export:</strong> Request a copy of your data (coming soon)</li>
              <li><strong>Revoke Access:</strong> Leave shared scenarios or remove collaborators</li>
            </ul>
          </section>

          <section className="content-section">
            <h2>Cookies and Local Storage</h2>
            <p>
              Ordelio uses local browser storage (IndexedDB) to enable offline functionality. 
              This data is stored on your device and is not transmitted to our servers except 
              when syncing your changes. We also use session cookies for authentication.
            </p>
          </section>

          <section className="content-section">
            <h2>Data Retention</h2>
            <p>
              We retain your data as long as your account is active. If you delete your account, 
              we will remove your personal data within 30 days, except where we must retain it 
              for legal obligations.
            </p>
          </section>

          <section className="content-section">
            <h2>Children's Privacy</h2>
            <p>
              Ordelio is not intended for use by children under 13 years of age. We do not 
              knowingly collect personal information from children under 13.
            </p>
          </section>

          <section className="content-section">
            <h2>Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any 
              significant changes by posting a notice in the app or sending an email to your 
              registered address.
            </p>
          </section>

          <section className="content-section">
            <h2>Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or how we handle your data, 
              please contact us at:
            </p>
            <p><strong>Email:</strong> privacy@ordelio.com</p>
          </section>

          <div className="cta-container">
            <button onClick={() => navigate('/')} className="cta-button">
              Back to Home
            </button>
          </div>
        </article>
      </main>

      <footer className="static-footer">
        <p>© 2026 Ordelio. All rights reserved.</p>
      </footer>
    </div>
  )
}
